import { ExternalLink, MessageCircle, Monitor, QrCode, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useWhatsAppAnchoredWindow } from "@/modules/despacho/useWhatsAppAnchoredWindow";
import { useWhatsAppHybridBridge } from "@/modules/despacho/useWhatsAppHybridBridge";

/**
 * Recuadro WhatsApp Web: prioriza modo híbrido (bridge Windows) si está activo en la PC.
 * Fallback: ventana popup anclada o pestaña nueva.
 */
export default function WhatsAppWebAnchoredPanel({ compact = false, className = "" }) {
  const {
    anchorRef,
    isOpen,
    syncBlocked,
    openAnchored,
    closePopup,
    openInNewTab,
  } = useWhatsAppAnchoredWindow({ enabled: true });

  const {
    bridgeAvailable,
    bridgeRunning,
    loading: bridgeLoading,
    opening: hybridOpening,
    lastError: hybridError,
    openHybrid,
    focusHybrid,
  } = useWhatsAppHybridBridge({ enabled: true });

  const minHeight = compact ? "min-h-[380px]" : "min-h-[520px]";
  const useHybrid = bridgeAvailable;

  return (
    <section
      className={`flex flex-col rounded-sm border border-slate-200 bg-slate-100 overflow-hidden ${minHeight} ${className}`}
      data-testid="whatsapp-web-anchored-panel"
    >
      <header className="shrink-0 flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-[#128C7E] text-white">
        <div className="flex items-center gap-2 min-w-0">
          <MessageCircle className="h-4 w-4 shrink-0" aria-hidden />
          <span className="text-xs font-semibold truncate">WhatsApp Web</span>
          {useHybrid ? (
            <span className="text-[10px] bg-white/20 rounded-sm px-1.5 py-0.5">Modo híbrido (PC)</span>
          ) : null}
          {!useHybrid && isOpen ? (
            <span className="text-[10px] bg-white/20 rounded-sm px-1.5 py-0.5">Ventana anclada</span>
          ) : null}
          {useHybrid && bridgeRunning ? (
            <span className="text-[10px] bg-emerald-500/40 rounded-sm px-1.5 py-0.5">Activo</span>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-1">
          {useHybrid ? (
            <>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="rounded-sm h-7 text-[10px] bg-white text-[#128C7E] hover:bg-white/90"
                onClick={bridgeRunning ? focusHybrid : openHybrid}
                disabled={hybridOpening}
                data-testid="whatsapp-hybrid-open"
              >
                <Monitor className="h-3 w-3 mr-1" aria-hidden />
                {bridgeRunning ? "Traer WhatsApp" : "Abrir WhatsApp híbrido"}
              </Button>
            </>
          ) : !isOpen ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="rounded-sm h-7 text-[10px] bg-white text-[#128C7E] hover:bg-white/90"
              onClick={openAnchored}
              data-testid="whatsapp-web-connect-anchored"
            >
              <QrCode className="h-3 w-3 mr-1" aria-hidden />
              Conectar (QR)
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="rounded-sm h-7 text-[10px] bg-white/15 text-white hover:bg-white/25 border border-white/30"
              onClick={closePopup}
              data-testid="whatsapp-web-close-anchored"
            >
              <X className="h-3 w-3 mr-1" aria-hidden />
              Cerrar ventana
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="rounded-sm h-7 text-[10px] text-white hover:bg-white/15"
            onClick={openInNewTab}
            data-testid="whatsapp-web-open-tab"
          >
            <ExternalLink className="h-3 w-3 mr-1" aria-hidden />
            Pestaña nueva
          </Button>
        </div>
      </header>

      <div
        ref={anchorRef}
        className={`relative flex-1 flex flex-col items-center justify-center p-4 text-center ${
          useHybrid
            ? "bg-[#ECE5DD]/50 border-2 border-dashed border-[#128C7E]/40"
            : isOpen
              ? "bg-[#ECE5DD]/40 border-2 border-dashed border-[#128C7E]/50"
              : "bg-[#ECE5DD]/60"
        }`}
        data-testid="whatsapp-web-anchor-slot"
      >
        {useHybrid ? (
          <div className="space-y-3 max-w-md">
            <div className="mx-auto w-16 h-16 rounded-full bg-white border border-slate-200 flex items-center justify-center">
              <Monitor className="h-8 w-8 text-[#128C7E]" aria-hidden />
            </div>
            <p className="text-sm font-semibold text-[#0F2D5C] m-0">Modo híbrido detectado</p>
            <p className="text-xs text-slate-600 m-0 leading-relaxed">
              <strong>Visor WhatsApp Bridge</strong> está activo en esta PC. Pulse{" "}
              <strong>Abrir WhatsApp híbrido</strong> para la ventana oficial con QR. El chat no va dentro del
              navegador, pero queda en una ventana dedicada de Edge en el mismo equipo.
            </p>
            {bridgeLoading ? (
              <p className="text-[11px] text-slate-500 m-0">Comprobando bridge…</p>
            ) : null}
            {hybridError ? (
              <p className="text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded-sm px-2 py-1 m-0">
                {hybridError}
              </p>
            ) : null}
            <p className="text-[10px] text-slate-500 m-0">
              Instalación: carpeta <code className="text-[10px]">dist_windows/whatsapp_bridge/</code> en el repo —
              ejecute <strong>VisorWhatsAppBridge.exe</strong> o <strong>run_bridge.bat</strong> en Windows.
            </p>
          </div>
        ) : isOpen ? (
          <div className="space-y-2 max-w-sm">
            <p className="text-sm font-semibold text-[#0F2D5C] m-0">WhatsApp Web activo</p>
            <p className="text-xs text-slate-600 m-0 leading-relaxed">
              La ventana oficial está anclada sobre este recuadro. Escanee el QR en esa ventana con WhatsApp
              Business del despacho.
            </p>
            {syncBlocked ? (
              <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 m-0">
                El navegador limitó el reposicionamiento. Mueva la ventana manualmente o use modo híbrido en Windows.
              </p>
            ) : null}
            <Button type="button" size="sm" variant="outline" className="rounded-sm h-8 text-xs" onClick={openAnchored}>
              Traer ventana al frente
            </Button>
          </div>
        ) : (
          <div className="space-y-3 max-w-md">
            <div className="mx-auto w-16 h-16 rounded-full bg-white border border-slate-200 flex items-center justify-center">
              <QrCode className="h-8 w-8 text-[#128C7E]" aria-hidden />
            </div>
            <p className="text-sm font-semibold text-[#0F2D5C] m-0">Conectar WhatsApp Business</p>
            <p className="text-xs text-slate-600 m-0 leading-relaxed">
              En <strong>Windows</strong>, instale y ejecute <strong>Visor WhatsApp Bridge</strong> en esta PC para
              modo híbrido (recomendado). Sin bridge: use <strong>Conectar (QR)</strong> o <strong>Pestaña nueva</strong>.
            </p>
            <ol className="text-left text-[11px] text-slate-600 m-0 pl-4 space-y-1">
              <li>Ejecute el bridge (.exe) en la PC del auxiliar.</li>
              <li>Abra WhatsApp híbrido o pestaña nueva.</li>
              <li>Escanee QR con WhatsApp Business del despacho.</li>
            </ol>
          </div>
        )}
      </div>
    </section>
  );
}
